import React, { useEffect, useState } from "react";
import { Collapse, Icon, Switch, Table, Tag, Button, message, Modal } from "antd";
import UserContext from "auth/UserContext";
import { useUser } from "auth/UserHooks";
import SkillCard from "components/skills/SkillCard";
import CardCollapse from "components/common/collapse/CardCollapse";
import AddNewCard from "components/common/AddNewCard";
import { Link } from "react-router-dom";
import formatDate from "utils/formatDate";
import EmployeeProjectTimeline from "./EmployeeProjectTimeline";
import EmployeeCertificationPanel from "./EmployeeCertificationPanel";
import PropTypes from "prop-types";
import EmployeeKeyMetrics from "./EmployeeKeyMetrics";
import CareerCoachCard from "../careerCoach/CareerCoachCard";
import EmployeeEntityCard from "../entities/EmployeeEntityCard";
import RoleTypeCard from "../RoleTypes/RoleTypeCard";
import { employeeType } from "types";
import { useFeatures } from "hooks/FeatureHooks";
import MajorAndMinorCard from "components/majorMinor/MajorAndMinorCard";
import MajorOrMinorCard from "components/majorMinor/MajorOrMinorCard";
import { getEmployee } from "api/employeeApi";
import { getEmployeePrompts } from "../../api/employeePromptApi";
import PromptCard from "../preferences/PromptCard";
import PromptCardModal from "../preferences/PromptCardModal";
import displayErrorNotification from "../../utils/displayErrorNotification";
import { fetchProfileCompletion } from "../../redux";
import store from "../../redux/store";
import sorters from "../../utils/sorters";
import {getEmployeeResumes, downloadEmployeeResume, deleteEmployeeResume} from "api/resumeApi";
import ResumeUploadModal from "../resume/ResumeUploadModal";
import Stack from "../common/Stack";
import {formatDateTime} from "../../utils/formatDateTime";

const EmployeeDetails = ({ employee = {}, projects = [] }) => {
  const staffingPreferences = "STAFFING_PREFERENCES";
  const additionalInformation = "ADDITIONAL_INFORMATION";

  const user = useUser();
  const features = useFeatures();
  const isMe = employee.id === user.id;
  const isCoach = user.coachees.includes(employee.id);
  const isInPractice = user.practice === employee.practice;
  const activeRoleTypes = employee.roleTypes.filter(r => r.roleType.active);
  const { skills } = employee;
  const [major, setMajor] = useState(employee.major);
  const [minor, setMinor] = useState(employee.minor);

  // Prompt Panels
  const [prompts, setPrompts] = useState([]);
  const [modalPrompt, setModalPrompt] = useState(null);
  const [isModalHidden, setIsModalHidden] = useState(false);
  const [modalEmployeePromptValue, setModalEmployeePromptValue] = useState("");
  const [certifications, setCertifications] = useState(employee.certifications);
  const [staffingPreferencesTag, setStaffingPreferencesTag] = useState(null);
  const [ isResumeUploadVisible, setIsResumeUploadVisible ] = useState( false );
  const [additionalInformationTag, setAdditionalInformationTag] = useState(
    null
  );
  const [resumes, setResumes] = useState( [] );


  const newSkillEmployeeLink = {
    pathname: "/employee/skills/new",
    state: { employeeId: employee.id }
  };
  const loadResumes = async () => {
    try {
      const data = await getEmployeeResumes( employee.id );
      setResumes( data );
    } catch (e) {
      displayErrorNotification( e );
    }
  };



  const newRoleTypeEmployeeLink = {
    pathname: "/employee/role-types/new",
    state: { employeeId: employee.id }
  };

  const _createTag = (prompts, category) => {
    const categoryPrompts = prompts.filter(p =>
      p.prompt.category?.includes(category)
    );
    const answeredPrompts = categoryPrompts.filter(cp => cp.response).length;
    return (
      <Tag color={answeredPrompts === categoryPrompts.length ? "" : "red"}>
        {`${answeredPrompts} of ${categoryPrompts.length} Answered`}
      </Tag>
    );
  };

  const _loadPromptData = () => {
    setIsModalHidden(false);
    setModalPrompt(null);
    setModalEmployeePromptValue("");

    getEmployeePrompts(employee.id)
      .then(resp => {
        setPrompts(resp);

        setStaffingPreferencesTag(_createTag(resp, staffingPreferences));
        setAdditionalInformationTag(_createTag(resp, additionalInformation));
      })
      .catch(displayErrorNotification);
  };

  useEffect(() => {
    _loadPromptData();
  }, []);

  useEffect( () => {
    if (employee?.id) {
      loadResumes();
    }
  }, [employee.id]);

  const updateEmployee = async () => {
    const newEmployee = await getEmployee(employee.id);
    setMajor(newEmployee.major);
    setMinor(newEmployee.minor);
    return newEmployee;
  };

  const editEmployeeMajorMinor = {
    pathname: `/employee/major-minor/${employee.id}`,
    state: { employeeId: employee.id, employee: employee }
  };

  let activePanels = [
    "aboutMe",
    "staffingPreferences",
    "additionalInformation"
  ];

  if (!Object.keys(employee).length) return null;

  const levelToValue = exLevel => {
    switch (exLevel) {
      case "ADVANCED":
        return 3;
      case "INTERMEDIATE":
        return 2;
      case "BEGINNER":
        return 1;
      default:
        return 0;
    }
  };
  const sortByLvAndDate = (a, b) =>
    levelToValue(b.experienceLevel) - levelToValue(a.experienceLevel) ||
    new Date(b.lastUsedDate) - new Date(a.lastUsedDate);

  const _handleChangeModal = prompt => {
    if (prompt) {
      setModalPrompt(prompt.prompt);
      setModalEmployeePromptValue(prompt.value);
    }
    setIsModalHidden(!isModalHidden);
  };

  const closeModal = () => {
    setModalPrompt(null);
    setModalEmployeePromptValue("");
    setIsModalHidden(false);
  };

  const handleDownload = async ( resume ) => {
    try {
      const arrayBuffer = await downloadEmployeeResume( resume.id );
      const blob = new Blob( [ arrayBuffer ], {
        type: "application/octet-stream",
      } );

      const url = window.URL.createObjectURL( blob );

      const link = document.createElement( "a" );
      link.href = url;
      link.download = resume.fileName;

      document.body.appendChild( link );
      link.click();

      link.remove();
      window.URL.revokeObjectURL( url );
    } catch (error) {
      console.error( "Download error:", error );
      message.error( "Failed to download resume" );
    }
  };

  const handleResumeDelete = ( resume ) => {
    Modal.confirm( {
      title: "Delete Resume",
      content: `Are you sure you want to delete ${ resume.fileName }?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteEmployeeResume( resume.id );
          message.success( "Resume deleted" );
          loadResumes();
        } catch (e) {
          message.error( "Failed to delete resume" );
        }
      },
    } );
  };


  if (!prompts || prompts.length === 0 || !employee) {
    return null;
  }



  return (
    <UserContext.Consumer>
      { ctx => {
        const canViewResume = ctx.user.permissions.canViewResume;
        const canUploadResume = ctx.user.permissions.canUploadResume;
        const canUploadEmployeeResume = ctx.user.permissions.canUploadEmployeeResume;
        const canDeleteEmployeeResume = ctx.user.permissions.canDeleteEmployeeResume;
        const isOwnProfile = ctx.user.id === employee.id;
        const canUploadResumeButton = isOwnProfile || canUploadEmployeeResume;
        const canDeleteResumeButton = isOwnProfile || canDeleteEmployeeResume;

        const resumeColumns = [
          {
            title: "File Name",
            dataIndex: "fileName",
            key: "fileName",
            width: "20%",
            align: "left",
          },
          {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: "18%",
            align: "center",
          },
          {
            title: "Uploaded On",
            dataIndex: "uploadDate",
            key: "uploadDate",
            width: "20%",
            render: date => formatDateTime( date ),
          },
          {
            title: "Comment",
            dataIndex: "comments",
            key: "comments",
            width: "25%",
          },
          {
            title: "Actions",
            key: "actions",
            width: "15%",
            align: "center",
            render: ( _, record ) => (
              <div style={ {display: "flex", gap: 8, justifyContent: "center"} }>
                <Button
                  icon={ "download" }
                  onClick={ () => handleDownload( record ) }
                >
                  Download
                </Button>

                {canDeleteResumeButton && (
                  <Button
                    icon={ "delete" }
                    onClick={ () => handleResumeDelete( record ) }
                  >
                    Delete
                  </Button>
                )}
              </div>
            ),
          },

        ];

        const TA_DIGITAL_ENTITY_ID = 6;

        const showResumePanel =
          canViewResume &&
          canUploadResume &&
          employee.entity.id === TA_DIGITAL_ENTITY_ID;

        return (
        <>
          <PromptCardModal
            modalState={isModalHidden}
            prompt={modalPrompt}
            employeePromptValue={modalEmployeePromptValue}
            employeeId={employee.id}
            closeModal={closeModal}
            category={modalPrompt?.category}
            onSubmit={() => {
              _loadPromptData();
              store.dispatch(fetchProfileCompletion(employee.id));
            }}
          />
          <Collapse bordered={false} defaultActiveKey={activePanels}>
            {((ctx.user.permissions.canUpdateOwnPromptResponse && isMe) ||
              (isCoach &&
                ctx.user.permissions.canUpdateCoacheePromptResponse) ||
              ctx.user.permissions.canReadPromptResponses) && (
              <CardCollapse.Panel
                header={
                  <>
                    <Icon type="question-circle" /> About Me
                  </>
                }
                key="aboutMe"
              >
                {ctx.user.permissions.canManageEmployees && (
                  <EmployeeEntityCard
                    employee={employee}
                    isEditable={ctx.user.permissions.canManageEmployees}
                  />
                )}
                {(!!employee.coach || employee.coachees.length > 0) && (
                  <CareerCoachCard employee={employee} />
                )}
                <MajorAndMinorCard
                  employee={employee}
                  isMe={isMe}
                  canEdit={ctx.user.permissions.canCreateMajorMinor}
                  isAdmin={ctx.user.permissions.canManageEmployees}
                  onCloseForm={updateEmployee}
                />
              </CardCollapse.Panel>
            )}
            <CardCollapse.Panel
              header={
                <>
                  <Icon type="schedule" /> {isMe ? "My " : ""}Staffing
                  Preferences
                </>
              }
              key="staffingPreferences"
              extra={staffingPreferencesTag}
            >
              {prompts.map(prompt =>
                prompt.prompt.category?.includes(staffingPreferences) ? (
                  <>
                    <CardCollapse.Card key={prompt.prompt.id}>
                      <PromptCard
                        EmplPrompt={prompt}
                        onSelect={() => _handleChangeModal(prompt)}
                      />
                    </CardCollapse.Card>
                  </>
                ) : null
              )}
            </CardCollapse.Panel>
            { showResumePanel && (
              <CardCollapse.Panel
                key="resume"
                header={
                  <>
                    <Icon type="file-text"/> { isMe ? "My " : "" }Resume
                  </>
                }
              >
                <div style={ {
                  width: "100vw",
                  marginLeft: "-32px",
                  marginRight: "-32px",
                } }>
                  <div style={ {padding: "0 32px"} }>
                    <Stack spacing="medium">
                    {canUploadResumeButton && (
                      <Button
                        type="primary"
                        onClick={() => setIsResumeUploadVisible(true)}
                      >
                        Upload Resume
                      </Button>
                    )}

                      <Table
                        rowKey={ record => record.id }
                        columns={ resumeColumns }
                        dataSource={ resumes }
                        pagination={ false }
                      />
                      { isResumeUploadVisible && (
                        <ResumeUploadModal
                          modalState={ isResumeUploadVisible }
                          employeeId={ employee.id }
                          refresh={ loadResumes }
                          closeModal={ () => setIsResumeUploadVisible( false ) }
                        />
                      ) }
                    </Stack>
                  </div>
                </div>
              </CardCollapse.Panel>
            ) }


            <CardCollapse.Panel
              header={
                <>
                  <Icon type="info-circle" /> {isMe ? "My " : ""}Additional
                  Information
                </>
              }
              key="additionalInformation"
              extra={additionalInformationTag}
            >
              {prompts.map(prompt =>
                prompt.prompt.category?.includes(additionalInformation) ? (
                  <>
                    <CardCollapse.Card key={prompt.prompt.id}>
                      <PromptCard
                        EmplPrompt={prompt}
                        onSelect={() => _handleChangeModal(prompt)}
                      />
                    </CardCollapse.Card>
                  </>
                ) : null
              )}
            </CardCollapse.Panel>
            {features &&
              !features.isEnabled("DISABLE_KEY_METRICS") &&
              (ctx.user.permissions.canReadKeyMetrics ||
                (isMe && ctx.user.permissions.canReadOwnKeyMetrics) ||
                (isInPractice &&
                  ctx.user.permissions.canReadPracticeKeyMetrics) ||
                (isCoach && ctx.user.permissions.canReadCoacheeKeyMetrics)) && (
                <CardCollapse.Panel
                  header={
                    <>
                      <Icon type="key" /> {isMe ? "My " : ""}Key Metrics
                    </>
                  }
                  key="keyMetrics"
                >
                  <EmployeeKeyMetrics employeeId={employee.id} />
                </CardCollapse.Panel>
              )}

            {(major || minor || ctx.user.permissions.canCreateMajorMinor) &&
              ctx.user.permissions.canReadMajorMinor && (
                <CardCollapse.Panel
                  key="majorMinor"
                  header={
                    <>
                      <Icon type="bulb" /> {isMe ? "My " : ""}Major and Minor
                    </>
                  }
                  extra={
                    <Tag>
                      {major && minor
                        ? "major and minor"
                        : major || minor
                        ? "major or minor"
                        : "no major or minor"}
                    </Tag>
                  }
                >
                  {(isMe || ctx.user.permissions.canReadMajorMinor) && major ? (
                    <CardCollapse.Card key={major.id}>
                      <MajorOrMinorCard
                        majorMinor={major}
                        major={"Major"}
                        isCapability={major.isCapability}
                      />
                    </CardCollapse.Card>
                  ) : null}
                  {(isMe || ctx.user.permissions.canReadMajorMinor) && minor ? (
                    <CardCollapse.Card key={minor.id}>
                      <MajorOrMinorCard
                        majorMinor={minor}
                        major={"Minor"}
                        isCapability={minor.isCapability}
                      />
                    </CardCollapse.Card>
                  ) : null}
                  {ctx.user.permissions.canCreateMajorMinor && (
                    <CardCollapse.Card>
                      <AddNewCard
                        title="Edit Major and Minor"
                        link={editEmployeeMajorMinor}
                      />
                    </CardCollapse.Card>
                  )}
                </CardCollapse.Panel>
              )}

            {ctx.user.permissions.canReadSkills && (
              <CardCollapse.Panel
                key="skills"
                header={
                  <>
                    <Icon type="compass" /> {isMe ? "My " : ""}Skills
                  </>
                }
                extra={
                  <Tag>
                    {skills && skills.length} Skill
                    {skills && skills.length === 1 ? "" : "s"}
                  </Tag>
                }
              >
                {(isMe || ctx.user.permissions.canReadSkills) && skills
                  ? skills.sort(sortByLvAndDate).map(skill => (
                      <CardCollapse.Card key={skill.skill.id}>
                        <SkillCard
                          employeeId={employee.id}
                          skill={skill.skill}
                          employeeSkill={skill}
                        />
                      </CardCollapse.Card>
                    ))
                  : null}
                {((ctx.user.permissions.canCreateOwnSkill && isMe) ||
                  (isCoach && ctx.user.permissions.canCreateCoacheeSkill) ||
                  ctx.user.permissions.canCreateSkill) && (
                  <CardCollapse.Card>
                    <AddNewCard title="Add skill" link={newSkillEmployeeLink} />
                  </CardCollapse.Card>
                )}
                {!isMe && (!skills || !skills.length) && "No skills"}
              </CardCollapse.Panel>
            )}

            {ctx.user.permissions.canReadRoleTypes && (
              <CardCollapse.Panel
                key="activeRoleTypes"
                header={
                  <>
                    <Icon type="team" /> {isMe ? "My " : ""}Role Types
                  </>
                }
                extra={
                  <Tag>
                    {activeRoleTypes && activeRoleTypes.length} Role Type
                    {activeRoleTypes && activeRoleTypes.length === 1 ? "" : "s"}
                  </Tag>
                }
              >
                {(isMe || ctx.user.permissions.canReadRoleTypes) &&
                activeRoleTypes
                  ? activeRoleTypes.sort(sortByLvAndDate).map(roleType => (
                      <CardCollapse.Card key={roleType.roleType.id}>
                        <RoleTypeCard
                          employeeId={employee.id}
                          roleType={roleType.roleType}
                          employeeRoleType={roleType}
                        />
                      </CardCollapse.Card>
                    ))
                  : null}
                {((ctx.user.permissions.canCreateOwnRoleType && isMe) ||
                  (isCoach && ctx.user.permissions.canCreateCoacheeRoleType) ||
                  ctx.user.permissions.canCreateRoleType) && (
                  <CardCollapse.Card>
                    <AddNewCard
                      title="Add Role Type"
                      link={newRoleTypeEmployeeLink}
                    />
                  </CardCollapse.Card>
                )}
                {!isMe &&
                  (!activeRoleTypes || !activeRoleTypes.length) &&
                  "No Role Types"}
              </CardCollapse.Panel>
            )}

            <CardCollapse.Panel
              key="certifications"
              header={
                <>
                  <Icon type="solution" /> {isMe ? "My " : ""}Certifications
                </>
              }
              extra={
                <Tag>
                  {certifications.length}{" "}
                  {certifications.length === 1
                    ? "certification"
                    : "certifications"}
                </Tag>
              }
            >
              <EmployeeCertificationPanel
                employeeId={employee.id}
                isMe={isMe}
                isCoach={isCoach}
                callback={setCertifications}
              />
            </CardCollapse.Panel>

            {projects &&
              projects.length &&
              ctx.user.permissions.canReadProjects && (
                <ProjectsDetails projects={projects} employee={employee} />
              )}
          </Collapse>
        </>
                );
            }}
    </UserContext.Consumer>
  );
};

EmployeeDetails.defaultProps = {
  employee: {}
};

EmployeeDetails.propTypes = {
  employee: employeeType
};

export default EmployeeDetails;

const ProjectsDetails = ({ projects = [], employee = {}, ...props }) => {
  const [showProjectTimeline, setShowProjectTimeline] = useState(false); // TODO: Remember user's preference in local storage
  const user = useUser();
  const isMe = employee.id === user.id;
  const projectColumns = [
    {
      title: "Client",
      dataIndex: "project.client",
      render: record => <Link to={`/clients/${record.id}`}>{record.name}</Link>
    },
    {
      title: "Project",
      dataIndex: "project.name",
      render: (name, record) => (
        <Link to={`/projects/${record.project.id}`}>{name}</Link>
      )
    },
    {
      title: "Projector Code",
      key: "projectorCode",
      render: record =>
        record.project.projectorProjectUID ? (
          <a
            href={`https://app.projectorpsa.com/x/ProjectLanding/${
              record.project.projectorProjectUID
            }`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {record.project.projectorCode}
          </a>
        ) : (
          <span>{record.project.projectorCode || "N/A"}</span>
        ),
      sorter: (a, b) =>
        sorters.string(a.project.projectorCode, b.project.projectorCode)
    },
    {
      title: "Role",
      dataIndex: "role"
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      render: formatDate,
      sorter: (a, b) => (a.startDate < b.startDate ? -1 : 1)
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      render: formatDate,
      defaultSortOrder: "descend",
      sorter: (a, b) => (a.endDate < b.endDate ? -1 : 1)
    },
    {
      title: "Hours / Week",
      dataIndex: "hoursPerWeek",
      width: 120,
      align: "right"
    },
    {
      title: "Active",
      dataIndex: "active",
      align: "center",
      render: record => (
        <Tag color={record ? "green" : "red"}>
          {record ? "Active" : "Inactive"}
        </Tag>
      ),
      sorter: (a, b) => (a.active < b.active ? -1 : 1)
    }
  ];

  const onChangeShowProjectTimeline = (checked, e) => {
    e.stopPropagation();
    setShowProjectTimeline(checked);
  };

  return (
    <>
      {projects && projects.length ? (
        <CardCollapse.Panel
          {...props}
          key="projects"
          header={
            <>
              <Icon type="project" /> {isMe ? "My " : ""}Projects
            </>
          }
          extra={
            <>
              <Switch
                checkedChildren="Table"
                unCheckedChildren="Timeline"
                defaultChecked={showProjectTimeline}
                onChange={onChangeShowProjectTimeline}
              />
              &nbsp;
              <Tag>
                {projects.length} Project{projects.length === 1 ? "" : "s"}
              </Tag>
            </>
          }
        >
          {showProjectTimeline && projects.length && (
            <EmployeeProjectTimeline projects={projects} />
          )}
          {!showProjectTimeline && (
            <Table
              style={{ width: "100%" }}
              pagination={{ position: "none", pageSize: projects.length }}
              rowKey={record => record.project.id}
              columns={projectColumns}
              dataSource={projects}
            />
          )}
        </CardCollapse.Panel>
      ) : null}
    </>
  );
};

ProjectsDetails.defaultProps = {
  projects: [],
  employee: {}
};

ProjectsDetails.propTypes = {
  employee: employeeType,
  projects: PropTypes.array
};
